// narrationDraftStore — sahne başına "taslak" AI anlatım sesi.
//
// AddSoundModal, sahne değişince AIVoiceTab'ı `key` ile yeniden mount ediyor;
// bileşen state'i (düzenlenmiş metin, üretilen ses, "Oluşturuluyor" durumu)
// bu yüzden kayboluyordu. Kullanıcı "Ses Oluştur"a basıp başka sahneye geçse
// bile üretim (fetch + job poll) arka planda sürer; sonucu buraya yazıyoruz ki
// sahneye geri dönüldüğünde taslak olarak (sarı) gösterilebilsin.
//
// Yalnızca bellek içi — sayfa yenilenince temizlenir. Kaydet (commit) veya
// "Taslağı at" ile sahnenin taslağı silinir.
//
// Draft şekli:
// { status: "generating" | "ready", text, originalText, edited, audioUrl,
//   duration, soundIndex, soundFile, voice, service, language, narratorMode,
//   elevenLabsModel, googleModel }

import { useSyncExternalStore } from "react";

let drafts = {};
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (e) {
      console.error("narrationDraftStore listener error", e);
    }
  });
}

export function getNarrationDraft(navId) {
  return (navId && drafts[navId]) || null;
}

export function getNarrationDrafts() {
  return drafts;
}

// `patch` mevcut taslağın üstüne yazılır; yeni bir obje üretilir ki
// useSyncExternalStore snapshot'ı değişsin.
export function setNarrationDraft(navId, patch) {
  if (!navId) return;
  drafts = { ...drafts, [navId]: { ...(drafts[navId] || {}), ...patch } };
  emit();
}

export function replaceNarrationDraft(navId, draft) {
  if (!navId) return;
  if (!draft) {
    clearNarrationDraft(navId);
    return;
  }
  drafts = { ...drafts, [navId]: draft };
  emit();
}

export function clearNarrationDraft(navId) {
  if (!navId || !drafts[navId]) return;
  const next = { ...drafts };
  delete next[navId];
  drafts = next;
  emit();
}

export function subscribeNarrationDrafts(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useNarrationDrafts() {
  return useSyncExternalStore(subscribeNarrationDrafts, getNarrationDrafts, getNarrationDrafts);
}

export function useNarrationDraft(navId) {
  const all = useNarrationDrafts();
  return (navId && all[navId]) || null;
}

// AI anlatım sesinin soundList girişi. AIVoiceTab.handleAddSound (canlı state)
// ve AddSoundModal "Tümünü onayla" (store'daki taslaklar) aynı şekli üretsin.
export function buildNarrationSoundData({
  file,
  name,
  startTime,
  duration,
  volumeLevel,
  narrationText,
  service,
  language,
  elevenLabsModel,
  voice,
  narratorMode,
  googleModel,
  geminiStyle,
  geminiPace,
  geminiAudioProfile,
}) {
  const isFlash3 = service === "google" && googleModel === "gemini-flash-3";
  const start = Number(startTime) || 0;
  return {
    file,
    name,
    startTime: start,
    endTime: start + (Number(duration) || 0),
    volumeLevel,
    narrationText,
    voiceData: {
      provider: narratorMode === "own" ? "private" : service,
      language,
      // ElevenLabs model seçimi — sahne tekrar açılınca geri yüklenir.
      elevenLabsModel: service === "elevenlabs" ? elevenLabsModel : undefined,
      voiceId: voice?.id || "",
      voiceName: voice?.name || "",
      narratorMode,
      googleModel: service === "google" ? googleModel : undefined,
      style: isFlash3 && geminiStyle ? geminiStyle : undefined,
      pace: isFlash3 && geminiPace ? geminiPace : undefined,
      audioProfile:
        isFlash3 && geminiAudioProfile && geminiAudioProfile.trim()
          ? geminiAudioProfile.trim()
          : undefined,
    },
  };
}

// Onaylanabilir taslak: ses hazır ve metin üretimden sonra değişmemiş.
export function isDraftApprovable(d) {
  return !!d && d.status === "ready" && !!d.audioUrl && !d.edited;
}

// Store'daki taslaktan soundList girişi. startTime 0 → addSound yeni sesi
// playhead'e taşır (yalnızca timeline'da açık sahne için).
export function draftToSoundData(d, sceneIndex) {
  return buildNarrationSoundData({
    file: d.audioUrl,
    name: d.soundName || `Scene-${sceneIndex}-narration.mp3`,
    startTime: d.startTime,
    duration: d.duration,
    volumeLevel: d.volumeLevel != null ? d.volumeLevel : 0.56,
    narrationText: d.text,
    service: d.service,
    language: d.language,
    elevenLabsModel: d.elevenLabsModel,
    voice: d.voice,
    narratorMode: d.narratorMode,
    googleModel: d.googleModel,
    geminiStyle: d.geminiStyle,
    geminiPace: d.geminiPace,
    geminiAudioProfile: d.geminiAudioProfile,
  });
}

// Test yardımcısı.
export function __resetNarrationDrafts() {
  drafts = {};
  emit();
}
